import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

export default function BusinessSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [canned, setCanned] = useState([]);
  const [newCanned, setNewCanned] = useState({ title: "", body: "" });
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", staff_role: "agent" });
  const [staffCreds, setStaffCreds] = useState(null);
  const { user } = useAuth();

  const loadCanned = () => api.get("/business/canned-replies").then(({ data }) => setCanned(data)).catch(() => {});
  const loadStaff = () => api.get("/business/staff").then(({ data }) => setStaff(data)).catch(() => {});

  useEffect(() => {
    api.get("/business/settings").then(({ data }) => setS(data)).catch((e) => toast.error(apiError(e)));
    loadCanned();
    loadStaff();
  }, []);

  const addStaff = async () => {
    try {
      const { data } = await api.post("/business/staff", newStaff);
      setStaffCreds({ email: newStaff.email, password: data.temp_password, email_status: data.email_status });
      setNewStaff({ name: "", email: "", staff_role: "agent" });
      loadStaff();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeStaff = async (uid) => {
    if (!window.confirm("Remove this staff member's access?")) return;
    try {
      await api.delete(`/business/staff/${uid}`);
      loadStaff();
      toast.success("Staff member removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addCanned = async () => {
    try {
      await api.post("/business/canned-replies", newCanned);
      setNewCanned({ title: "", body: "" });
      loadCanned();
      toast.success("Template saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeCanned = async (rid) => {
    await api.delete(`/business/canned-replies/${rid}`);
    loadCanned();
  };

  if (!s) return <p className="text-neutral-400">Loading…</p>;

  const setTop = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setNested = (section, k, v) => setS({ ...s, [section]: { ...s[section], [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business/settings", s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="business-settings-page">
      <h1 className="text-3xl font-black">Settings</h1>
      <p className="text-sm text-neutral-500 mt-1">
        Your own SMTP/Twilio override the platform defaults. Leave blank to use the site-wide fallback.
      </p>

      <Tabs defaultValue="notifications" className="mt-8">
        <TabsList className="bg-white border border-[#E5E5E5]" data-testid="settings-tabs">
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
          <TabsTrigger value="widget" data-testid="tab-widget">Live chat</TabsTrigger>
          <TabsTrigger value="smtp" data-testid="tab-smtp">SMTP</TabsTrigger>
          <TabsTrigger value="twilio" data-testid="tab-twilio">Twilio</TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6">
          <div className="space-y-2">
            <Label>Notification email (receives new submission alerts)</Label>
            <Input value={s.notification_email} onChange={setTop("notification_email")} placeholder="you@business.com" data-testid="notification-email-input" />
          </div>
          <div className="space-y-2">
            <Label>Notification phone (SMS alerts, E.164 e.g. +14155552671)</Label>
            <Input value={s.notify_phone} onChange={setTop("notify_phone")} placeholder="+44…" data-testid="notify-phone-input" />
          </div>
          <div className="space-y-2">
            <Label>Brand name (shown in emails)</Label>
            <Input value={s.brand_name} onChange={setTop("brand_name")} data-testid="brand-name-input" />
          </div>
          <div className="pt-4 border-t border-[#E5E5E5] space-y-4">
            <div className="flex items-center gap-3">
              <Switch checked={!!s.auto_reply_enabled} onCheckedChange={(v) => setS({ ...s, auto_reply_enabled: v })} data-testid="auto-reply-switch" />
              <Label>Auto-reply to new submissions</Label>
            </div>
            {s.auto_reply_enabled && (
              <Textarea
                rows={3}
                value={s.auto_reply_message || ""}
                onChange={(e) => setS({ ...s, auto_reply_message: e.target.value })}
                placeholder="Thanks for reaching out — we usually reply within one business day."
                data-testid="auto-reply-message-input"
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="team" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="team-tab">
          <p className="text-sm text-neutral-500">Owners get full access. Agents can manage forms, conversations and customers — but not settings, billing or staff.</p>
          <div className="space-y-3">
            {staff.map((m) => (
              <div key={m.id} className="border border-[#E5E5E5] rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`staff-${m.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {m.name}
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${m.staff_role === "agent" ? "border-neutral-200 bg-neutral-100 text-neutral-500" : "border-neutral-900 bg-neutral-900 text-white"}`} data-testid={`staff-role-${m.id}`}>
                      {m.staff_role === "agent" ? "Agent" : "Owner"}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-400">{m.email}</div>
                </div>
                {m.id !== user?.id && (
                  <Button variant="ghost" size="sm" className="text-red-600 shrink-0" onClick={() => removeStaff(m.id)} data-testid={`staff-remove-${m.id}`}>Remove</Button>
                )}
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-[#E5E5E5] space-y-3">
            <div className="grid grid-cols-[1fr_1fr_130px] gap-3">
              <Input placeholder="Staff name" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} data-testid="staff-name-input" />
              <Input type="email" placeholder="Staff email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} data-testid="staff-email-input" />
              <Select value={newStaff.staff_role} onValueChange={(v) => setNewStaff({ ...newStaff, staff_role: v })}>
                <SelectTrigger data-testid="staff-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="agent" data-testid="staff-role-agent">Agent</SelectItem>
                  <SelectItem value="owner" data-testid="staff-role-owner">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-neutral-400">Agents can manage forms, conversations and customers. Only owners can change settings, billing and staff.</p>
            <Button variant="outline" className="rounded-full" onClick={addStaff} disabled={!newStaff.name || !newStaff.email} data-testid="staff-add-button">Add staff member</Button>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="templates-tab">
          <p className="text-sm text-neutral-500">Saved replies you can insert into any conversation from the composer.</p>
          <div className="space-y-3">
            {canned.map((c) => (
              <div key={c.id} className="border border-[#E5E5E5] rounded-xl p-4 flex items-start justify-between gap-4" data-testid={`canned-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{c.title}</div>
                  <p className="text-xs text-neutral-500 mt-1 whitespace-pre-wrap">{c.body}</p>
                </div>
                <Button variant="ghost" size="sm" className="text-red-600 shrink-0" onClick={() => removeCanned(c.id)} data-testid={`canned-delete-${c.id}`}>Delete</Button>
              </div>
            ))}
            {canned.length === 0 && <p className="text-sm text-neutral-400">No templates yet.</p>}
          </div>
          <div className="pt-4 border-t border-[#E5E5E5] space-y-3">
            <Input placeholder="Template title" value={newCanned.title} onChange={(e) => setNewCanned({ ...newCanned, title: e.target.value })} data-testid="canned-title-input" />
            <Textarea rows={3} placeholder="Template text…" value={newCanned.body} onChange={(e) => setNewCanned({ ...newCanned, body: e.target.value })} data-testid="canned-body-input" />
            <Button variant="outline" className="rounded-full" onClick={addCanned} disabled={!newCanned.title || !newCanned.body} data-testid="canned-add-button">Add template</Button>
          </div>
        </TabsContent>

        <TabsContent value="widget" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="widget-tab">
          <div className="flex items-center gap-3">
            <Switch checked={!!s.widget_enabled} onCheckedChange={(v) => setS({ ...s, widget_enabled: v })} data-testid="widget-enabled-switch" />
            <Label>Enable live chat widget</Label>
          </div>
          <p className="text-sm text-neutral-500">
            Chats land in your Conversations hub. Paste this snippet before the closing body tag of your website:
          </p>
          <div className="bg-neutral-900 text-neutral-100 rounded-xl p-4 text-xs font-mono break-all" data-testid="widget-embed-code">
            {`<script src="${window.location.origin}/api/public/widget.js" data-business="${user?.business_id}"></script>`}
          </div>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => {
              navigator.clipboard.writeText(`<script src="${window.location.origin}/api/public/widget.js" data-business="${user?.business_id}"></script>`);
              toast.success("Embed code copied");
            }}
            data-testid="widget-copy-button"
          >
            Copy embed code
          </Button>
        </TabsContent>

        <TabsContent value="smtp" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SMTP host</Label>
              <Input value={s.smtp.host || ""} onChange={(e) => setNested("smtp", "host", e.target.value)} placeholder="smtp.example.com" data-testid="smtp-host-input" />
            </div>
            <div className="space-y-2">
              <Label>Port</Label>
              <Input type="number" value={s.smtp.port || 587} onChange={(e) => setNested("smtp", "port", Number(e.target.value))} data-testid="smtp-port-input" />
              <p className="text-xs text-neutral-400">465 = SSL (automatic), 587/25 = STARTTLS</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={s.smtp.username || ""} onChange={(e) => setNested("smtp", "username", e.target.value)} data-testid="smtp-username-input" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={s.smtp.password || ""} onChange={(e) => setNested("smtp", "password", e.target.value)} data-testid="smtp-password-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From email</Label>
              <Input value={s.smtp.from_email || ""} onChange={(e) => setNested("smtp", "from_email", e.target.value)} placeholder="hello@business.com" data-testid="smtp-from-email-input" />
            </div>
            <div className="space-y-2">
              <Label>From name</Label>
              <Input value={s.smtp.from_name || ""} onChange={(e) => setNested("smtp", "from_name", e.target.value)} data-testid="smtp-from-name-input" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={s.smtp.use_tls !== false} onCheckedChange={(v) => setNested("smtp", "use_tls", v)} data-testid="smtp-tls-switch" />
            <Label>Use TLS/STARTTLS</Label>
          </div>
        </TabsContent>

        <TabsContent value="twilio" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6">
          <div className="space-y-2">
            <Label>Account SID</Label>
            <Input value={s.twilio.account_sid || ""} onChange={(e) => setNested("twilio", "account_sid", e.target.value)} placeholder="AC…" data-testid="twilio-sid-input" />
          </div>
          <div className="space-y-2">
            <Label>Auth token</Label>
            <Input type="password" value={s.twilio.auth_token || ""} onChange={(e) => setNested("twilio", "auth_token", e.target.value)} data-testid="twilio-token-input" />
          </div>
          <div className="space-y-2">
            <Label>From number (E.164)</Label>
            <Input value={s.twilio.from_number || ""} onChange={(e) => setNested("twilio", "from_number", e.target.value)} placeholder="+1…" data-testid="twilio-from-input" />
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!staffCreds} onOpenChange={(open) => !open && setStaffCreds(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="staff-credentials-dialog">
          <DialogHeader>
            <DialogTitle>Staff login created</DialogTitle>
          </DialogHeader>
          {staffCreds && (
            <div className="space-y-4">
              <div className="bg-neutral-50 border border-[#E5E5E5] rounded-xl p-4 text-sm space-y-1.5">
                <div>Email: <b data-testid="staff-creds-email">{staffCreds.email}</b></div>
                <div>Temporary password: <b data-testid="staff-creds-password">{staffCreds.password}</b></div>
              </div>
              <p className={`text-xs ${staffCreds.email_status === "sent" ? "text-emerald-700" : "text-amber-700"}`}>
                {staffCreds.email_status === "sent"
                  ? "The welcome email was sent to the staff member."
                  : "SMTP is not configured, so no email was delivered — share these credentials manually."}
              </p>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => {
                  navigator.clipboard.writeText(`Email: ${staffCreds.email}\nPassword: ${staffCreds.password}`);
                  toast.success("Copied to clipboard");
                }}
                data-testid="staff-creds-copy-button"
              >
                Copy credentials
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Button onClick={save} disabled={saving} className="mt-6 rounded-full px-8" data-testid="settings-save-button">
        {saving ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}
